import { SetStateAction } from "react";

import { useTranslation } from "react-i18next";

import ToggleItemWithTooltip from "../../../../../../ToggleItem/ToggleItemWithTooltip";
import {
  CreditsOverdraftSettingsPropsType,
  DriversSettingsPropsType,
  ToggleConfig,
  VehiclesSettingsPropsType
} from "../../../../../types";
import OverdraftInput from "../CompanySettings/components/OverdraftInput";

interface UseCompaniesSettingsProps {
  detailsData: any;
  setIsLoading?: (value: SetStateAction<boolean>) => void;
}

const useCompaniesSettings = ({ detailsData }: UseCompaniesSettingsProps) => {
  const { t } = useTranslation();

  const renderToggleList = (items: ToggleConfig[], isDisabled?: boolean) =>
    items.map(({ title, tooltipText, action, isChecked }) => (
      <ToggleItemWithTooltip
        key={title}
        title={title}
        tooltipText={tooltipText}
        onToggle={action}
        isChecked={isChecked}
        isDisabled={isDisabled}
        testId={title}
        customContainerStyle={{ paddingTop: "8px" }}
      />
    ));

  const generateDriversSettings = ({
    addNewDriver,
    handleToggleAddNewDriver,
    autoDriverFileDownload,
    handleToggleAutoDriverFileDownload,
    handleToggleAssignGlobalScheduleToDriver,
    assignGlobalScheduleToDriver,
    isDisabled
  }: DriversSettingsPropsType) =>
    renderToggleList(
      [
        {
          title: t("Dialog##company settings##auto add new drivers"),
          tooltipText: t(
            "Dialog##company settings##auto add new drivers tooltip"
          ),
          action: handleToggleAddNewDriver,
          isChecked: addNewDriver
        },
        {
          title: t("Dialog##company settings##auto download driver data"),
          tooltipText: t(
            "Dialog##company settings##auto download driver data tooltip"
          ),
          action: handleToggleAutoDriverFileDownload,
          isChecked: autoDriverFileDownload
        },
        {
          title: t(
            "Dialog##company settings##auto assign global schedule to drivers"
          ),
          tooltipText: t(
            "Dialog##company settings##auto assign global schedule to drivers tooltip"
          ),
          action: handleToggleAssignGlobalScheduleToDriver,
          isChecked: assignGlobalScheduleToDriver
        }
      ],
      isDisabled
    );

  const generateVehiclesSettings = ({
    assignVehicleRegistrationNumber,
    handleToggleAssignVehicleRegistrationNumber,

    autoInitialVehicleDownload,
    handleToggleAutoInitialVehicleDownload,

    assignGlobalScheduleToVehicle,
    handleToggleAssignGlobalScheduleToVehicle,
    isDisabled
  }: VehiclesSettingsPropsType) =>
    renderToggleList(
      [
        {
          title: t(
            "Dialog##company settings##auto update vehicle registration number"
          ),
          tooltipText: t(
            "Dialog##company settings##auto update vehicle registration number tooltip"
          ),
          isChecked: assignVehicleRegistrationNumber,
          action: handleToggleAssignVehicleRegistrationNumber
        },
        {
          title: t(
            "Dialog##company settings##automatic initial vehicle download"
          ),
          tooltipText: t(
            "Dialog##company settings##automatic initial vehicle download tooltip"
          ),
          isChecked: autoInitialVehicleDownload,
          action: handleToggleAutoInitialVehicleDownload
        },
        {
          title: t(
            "Dialog##company settings##auto assign global schedule to vehicles"
          ),
          tooltipText: t(
            "Dialog##company settings##auto assign global schedule to vehicles tooltip"
          ),
          isChecked: assignGlobalScheduleToVehicle,
          action: handleToggleAssignGlobalScheduleToVehicle
        }
      ],
      isDisabled
    );

  const generateCreditsOverdraftSettings = ({
    handleToggleCreditsOverdraft,
    isCreditsOverdraftChecked,
    setOverdraftInputValue,
    overdraftInputValue
  }: CreditsOverdraftSettingsPropsType) => (
    <ToggleItemWithTooltip
      title={t("Dialog##company settings##allow credits overdraft")}
      tooltipText={t("Dialog##company settings##credits overdraft tooltip")}
      onToggle={handleToggleCreditsOverdraft}
      isChecked={isCreditsOverdraftChecked}
      testId="credits-overdraft"
      customContainerStyle={{ paddingTop: "8px" }}
      embeddedElement={
        <OverdraftInput
          disabled={detailsData?.overDraftEnabled}
          setValue={setOverdraftInputValue}
          value={overdraftInputValue}
        />
      }
    />
  );

  return {
    generateDriversSettings,
    generateVehiclesSettings,
    generateCreditsOverdraftSettings
  };
};

export default useCompaniesSettings;
